import React from "react";
import { Stack, TextField } from "@mui/material";
import { useFormikContext } from "formik";

import FieldGroup from "../../../../shared/FieldGroup";
import type {
  FreeOperantProcedure as FreeOperantValues,
  TargetFormValues,
} from "../../TargetBuilderPanel.types";
import { isFreeOperantProcedure } from "../../TargetBuilderPanel.types";
import TeachingProcedureCommonFields from "./TeachingProcedureCommonFields";

interface FreeOperantProcedureProps {
  fieldsOptional?: boolean;
}

const FreeOperantProcedure: React.FC<FreeOperantProcedureProps> = ({ fieldsOptional }) => {
  const { values, setFieldValue } = useFormikContext<TargetFormValues>();
  if (!isFreeOperantProcedure(values.teaching_procedure)) return null;

  const procedure: FreeOperantValues = values.teaching_procedure;
  const update = (partial: Partial<FreeOperantValues>) =>
    setFieldValue("teaching_procedure", { ...procedure, ...partial });

  return (
    <Stack gap={4}>
      <TeachingProcedureCommonFields />

      <FieldGroup
        label="Operational Definition"
        description="Discrete responses recorded as they occur naturally, without arranged instructional prompts or programmed consequences"
        required={!fieldsOptional}
      >
        <TextField
          multiline
          minRows={3}
          fullWidth
          value={procedure.operational_definition}
          onChange={(e) => update({ operational_definition: e.target.value })}
          placeholder="e.g., 'Any audible consonant-vowel combination not preceded by a prompt within 5 seconds'"
          aria-label="Operational definition"
        />
      </FieldGroup>

      <FieldGroup
        label="Typical Contexts"
        description="Where is the behavior most likely to occur or be monitored?"
      >
        <TextField
          multiline
          minRows={3}
          fullWidth
          value={procedure.typical_contexts}
          onChange={(e) => update({ typical_contexts: e.target.value })}
          placeholder="e.g., 'During independent free play'"
          aria-label="Typical contexts"
        />
      </FieldGroup>

      <FieldGroup
        label="Antecedent Conditions"
        description="Conditions that reliably precede the behavior (if applicable)"
      >
        <TextField
          multiline
          minRows={3}
          fullWidth
          value={procedure.antecedent_conditions}
          onChange={(e) => update({ antecedent_conditions: e.target.value })}
          placeholder="e.g., 'Demand presentation, tangible removal, peer proximity'"
          aria-label="Antecedent conditions"
        />
      </FieldGroup>

      <FieldGroup
        label="Response / Consequence Notes"
        description="How the team responds when the behavior occurs"
      >
        <TextField
          multiline
          minRows={3}
          fullWidth
          value={procedure.response_consequence_notes}
          onChange={(e) => update({ response_consequence_notes: e.target.value })}
          placeholder="e.g., 'Planned ignoring, redirect to replacement behavior, safety protocol if needed'"
          aria-label="Response and consequence notes"
        />
      </FieldGroup>
    </Stack>
  );
};

export default FreeOperantProcedure;
